/**
 * Shared utility functions for calculating Bill Summary totals
 * This ensures Sum Analysis and individual summaries always match
 */

import {
  generatePlotMatrixColumns,
  generatePlotMatrixRows,
} from "./plotMatrixGenerator";
import { generateBOQSummaryWithCategories } from "./boqSummaryGenerator";
import { reconstructPlotDrivesColumnHeaders } from "./plotDrivesColumnMapping";
import { reconstructPlotPavingsColumnHeaders } from "./plotPavingsColumnMapping";
import {
  CUT_FILL_CATEGORIES,
  FOUNDATION_CATEGORIES,
  DRAINAGE_CATEGORIES,
  PLOT_DRIVES_CATEGORIES,
  PLOT_SERVICE_CATEGORIES,
  SERVICE_CONNECTIONS_CATEGORIES,
  PLOT_PAVINGS_CATEGORIES,
  SCAFFOLD_CATEGORIES,
  GARDEN_CATEGORIES,
  RET_SCREEN_WALLS_CATEGORIES,
} from "./billSummaryCategories";
import { RateMappingJSON } from "../types/rateMappingTypes";
import { ItemLibraryResponse } from "../api/itemLibrary";
import { createFoundationsRateLookupMap, applyFoundationsRatesToSummary } from "./foundationsItemLibraryIntegration";
import { createCutFillsRateLookupMap, applyCutFillsRatesToSummary } from "./cutFillsItemLibraryIntegration";
import { createDrainageRateLookupMap } from "./drainageItemLibraryIntegration";
import {
  OSMA_FOUL_COMBINED_FIELDS,
  OSMA_STORM_COMBINED_FIELDS,
  POLYPIPE_FOUL_COMBINED_FIELDS,
  POLYPIPE_STORM_COMBINED_FIELDS,
} from "./drainageReferenceMapping";
import { createPlotDrivesRateLookupMap, applyPlotDrivesRatesToSummary } from "./plotDrivesItemLibraryIntegration";
import { createPlotServiceRateLookupMap, applyPlotServiceRatesToSummary } from "./plotServiceItemLibraryIntegration";
import { createPlotPavingsRateLookupMap, applyPlotPavingsRatesToSummary } from "./plotPavingsItemLibraryIntegration";
import { createScaffoldRateLookupMap, applyScaffoldRatesToSummary } from "./scaffoldItemLibraryIntegration";
import { createGardenRateLookupMap, applyGardenRatesToSummary } from "./gardenItemLibraryIntegration";
import { createRetainingWallsRateLookupMap } from "./retainingWallsItemLibraryIntegration";
import { SCREEN_WALL_BOQ_FIELDS, getScreenWallFieldCode } from "./retainingWallsReferenceMapping";
import {
  wallSpecifications,
  WALLS_BUILD_UP_ITEM_CODE_MAP,
  resolveWallsBuildUpItemCode,
  isFacingBrickworkItem,
  isBlockworkBuildUpItem,
  getWallsBuildUpRateMultiplier,
} from "../mock/walls-section-build-up";
import { createGeoToberWallRateLookupMap } from "./geoToberWallItemLibraryIntegration";
import {
  geoToberWallSpecifications,
  geoToberWallFieldToSpecId,
  GEO_WALL_BUILD_UP_ITEM_CODE_MAP,
  TOBERMORE_BUILD_UP_ITEM_CODE_MAP,
} from "../mock/geo-tober-wall-build-up";
import { RetainingWallsBuildUpSpecification } from "../api/retainingWallsBuildUp";
import { GeoToberWallBuildUpSpecification } from "../api/wallSectionBuildUp";

/**
 * Calculate the exact grand total for a step - matches GenericBillSummary's summary row EXACTLY
 * This function now uses the BOQ approach (same as GenericBillSummary) instead of plot matrix
 */
export const calculateStepGrandTotal = (
  groundworksData: any,
  stepName: string,
  categories?: { [categoryName: string]: string[] },
  ratesMapping?: RateMappingJSON | null
): number => {
  try {
    // If categories provided, use BOQ approach with categories (matches GenericBillSummary)
    if (categories) {
      // Get dimension values for sections that need them (same as GenericBillSummary)
      const dimensionValues =
        stepName === "Plot Drives"
          ? groundworksData?.plotDrivesDimensionValues
          : stepName === "Plot Pavings"
          ? groundworksData?.plotPavingsDimensionValues
          : stepName === "Scaffold"
          ? groundworksData?.scaffoldDimensionValues
          : stepName === "Garden"
          ? groundworksData?.gardenDimensionValues
          : undefined;

      // Get column headers for sections that need them (same as GenericBillSummary)
      const columnHeaders =
        stepName === "Plot Drives"
          ? reconstructPlotDrivesColumnHeaders(
              groundworksData?.plotDrivesColumnHeaders || {},
              groundworksData?.plotDrivesDimensionValues || {}
            )
          : stepName === "Plot Pavings"
          ? reconstructPlotPavingsColumnHeaders(
              groundworksData?.plotPavingsColumnHeaders || {},
              groundworksData?.plotPavingsDimensionValues || {}
            )
          : stepName === "Scaffold"
          ? groundworksData?.scaffoldColumnHeaders
          : stepName === "Garden"
          ? groundworksData?.gardenColumnHeaders
          : undefined;

      return calculateBOQGrandTotal(
        groundworksData,
        stepName,
        categories,
        dimensionValues,
        ratesMapping,
        columnHeaders
      );
    }

    // If no categories, use old plot matrix approach (for backwards compatibility)
    const allColumns = generatePlotMatrixColumns(groundworksData);
    const stepColumns = allColumns.filter((col) => col.stepName === stepName);

    if (stepColumns.length === 0) return 0;

    const rows = generatePlotMatrixRows(groundworksData, allColumns);

    // Without categories - use all columns
    const summaryData: any[] = [];
    stepColumns.forEach((col, index) => {
      let totalQuantity = 0;
      let totalAmount = 0;

      rows.forEach((row) => {
        const cellData = row[col.dataIndex];
        if (cellData && typeof cellData === "object") {
          totalQuantity += cellData.quantity || 0;
          totalAmount += cellData.total || 0;
        }
      });

      if (totalQuantity > 0) {
        summaryData.push({
          key: col.key,
          itemNumber: `${index + 1}.0`,
          fieldName: col.fieldName,
          description: col.label,
          quantity: totalQuantity,
          unit: col.unit || "Nr",
          rate: col.rate,
          amount: totalAmount, // Store as NUMBER
        });
      }
    });

    // Calculate total
    const totalAmt = summaryData.reduce(
      (sum, item) => sum + (item.amount || 0),
      0
    );

    return totalAmt;
  } catch (error) {
    console.error(`Error calculating grand total for ${stepName}:`, error);
    return 0;
  }
};

/**
 * Helper function to calculate grand total from BOQ items with categories
 * This matches the exact logic used in all Bill Summary components
 * Now supports both item library (preferred) and legacy rate mapping
 */
const calculateBOQGrandTotal = (
  groundworksData: any,
  stepName: string,
  categories: { [categoryName: string]: string[] },
  dimensionValues?: Record<string, string>,
  ratesMapping?: RateMappingJSON | null,
  columnHeaders?: Record<string, string>,
  itemLibraryData?: ItemLibraryResponse,
  applyRatesFunction?: (items: any[], rateLookupMap: Map<string, any>) => any[],
  markupFactor?: number
): number => {
  try {
    // Generate BOQ summary items grouped by category
    let items = generateBOQSummaryWithCategories(
      groundworksData,
      stepName,
      categories,
      dimensionValues,
      ratesMapping,
      columnHeaders
    );

    // If item library data and apply rates function are provided, use item library rates
    if (itemLibraryData && applyRatesFunction) {
      // Create rate lookup map based on step name
      let rateLookupMap: Map<string, any> = new Map();

      switch (stepName) {
        case "Foundations":
          rateLookupMap = createFoundationsRateLookupMap(itemLibraryData, groundworksData);
          break;
        case "Cut & Fills":
          rateLookupMap = createCutFillsRateLookupMap(itemLibraryData);
          break;
        case "Drainage":
          rateLookupMap = createDrainageRateLookupMap(itemLibraryData);
          break;
        case "Plot Drives":
          rateLookupMap = createPlotDrivesRateLookupMap(itemLibraryData, groundworksData);
          break;
        case "Plot Service":
        case "Service Connections":
          rateLookupMap = createPlotServiceRateLookupMap(itemLibraryData);
          break;
        case "Plot Pavings":
          rateLookupMap = createPlotPavingsRateLookupMap(itemLibraryData, groundworksData);
          break;
        case "Scaffold":
          rateLookupMap = createScaffoldRateLookupMap(itemLibraryData, groundworksData);
          break;
        case "Garden":
          rateLookupMap = createGardenRateLookupMap(itemLibraryData, groundworksData);
          break;
        case "Ret & Screen Walls":
          rateLookupMap = createRetainingWallsRateLookupMap(
            itemLibraryData,
            groundworksData?.wallsBuildUpConfig?.screenWallsLabel,
            groundworksData?.wallsBuildUpConfig?.eoPillarLabel,
          );
          break;
        case "Geowall & Tobermore Walls":
          rateLookupMap = createGeoToberWallRateLookupMap(itemLibraryData);
          break;
      }

      // Apply item library rates
      items = applyRatesFunction(items, rateLookupMap);
    }

    // Apply plot service apartments quantity multiplier
    if (stepName === 'Plot Service' || stepName === 'Service Connections') {
      const config = groundworksData?.plotServiceConfig as { plotServicesApartmentsMultiple?: number; serviceConnectionApartmentsMultiple?: number } | undefined;
      if (config) {
        const plotMultiple = config.plotServicesApartmentsMultiple ?? 1;
        const scMultiple = config.serviceConnectionApartmentsMultiple ?? 1;
        if (plotMultiple !== 1 || scMultiple !== 1) {
          items = items.map((item: any) => {
            let multiple = 1;
            if (item.fieldName === 'apartments') multiple = plotMultiple;
            else if (item.fieldName === 'serviceConnectionApartments') multiple = scMultiple;
            return multiple === 1 ? item : { ...item, rate: (item.rate || 0) * multiple };
          });
        }
      }
    }

    // Process to rawProcessedData using the same qty/rate rounding as individual bill summaries:
    // roundedQty = Math.round(quantity), displayRate = parseFloat((rate * markupFactor).toFixed(2))
    const mk = markupFactor ?? 1;
    const rawProcessedData = items.map((item) => {
      const roundedQty = Math.round(item.quantity || 0);
      const displayRate = parseFloat(((item.rate || 0) as number * mk).toFixed(2));
      return {
        amount: Math.abs(roundedQty * displayRate).toFixed(2),
        hasData: true,
        isDiameterHeader: false,
      };
    });

    // Flatten data to include nested children items
    const flattenedData: any[] = [];
    rawProcessedData.forEach((item) => {
      if (!item.isDiameterHeader && item.hasData) {
        flattenedData.push(item);
      }
    });

    // Calculate total
    const totalAmt = flattenedData.reduce(
      (sum, item) => sum + parseFloat(item.amount || 0),
      0
    );

    return totalAmt;
  } catch (error) {
    console.error(`Error calculating ${stepName} grand total:`, error);
    return 0;
  }
};

/**
 * Calculate Foundations grand total - matches FoundationsBillSummary EXACTLY
 */
export const calculateFoundationsGrandTotal = (
  groundworksData: any,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  markupFactor?: number
): number => {
  if (
    !groundworksData?.foundationsTable ||
    groundworksData.foundationsTable.length === 0
  ) {
    return 0;
  }
  return calculateBOQGrandTotal(
    groundworksData,
    "Foundations",
    FOUNDATION_CATEGORIES,
    undefined,
    ratesMapping,
    undefined,
    itemLibraryData,
    applyFoundationsRatesToSummary,
    markupFactor
  );
};

/**
 * Calculate Cut & Fill grand total - matches GroundworksCutFillBillSummary EXACTLY
 * Includes Section 1.0 Site Clearance + Section 2.0 Cut & Fill + Section 3.0 Soil Management
 */
export const calculateCutFillGrandTotal = (
  groundworksData: any,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  markupFactor?: number
): number => {
  if (
    !groundworksData?.cutFillsTable ||
    groundworksData.cutFillsTable.length === 0
  ) {
    return 0;
  }

  // Sections 1.0 + 2.0
  const base = calculateBOQGrandTotal(
    groundworksData,
    "Cut & Fills",
    CUT_FILL_CATEGORIES,
    undefined,
    ratesMapping,
    undefined,
    itemLibraryData,
    applyCutFillsRatesToSummary,
    markupFactor
  );

  // Section 3.0 Soil Management — mirrors GroundworksCutFillBillSummary processedData logic
  const soilTotal = (() => {
    if (!itemLibraryData) return 0;

    const mk = markupFactor ?? 1;

    // Build code→rate lookup (handles both array and {items:[]} API shapes)
    const items: any[] = Array.isArray(itemLibraryData)
      ? (itemLibraryData as unknown as any[])
      : ((itemLibraryData as ItemLibraryResponse).items || []);
    const codeRateLookup = new Map<string, number>();
    items.forEach((item: any) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) codeRateLookup.set(code, item.nettRate || item.baseRate || 0);
    });

    // --- 3.1 Topsoil ---
    const cutFillsTable: any[] = groundworksData?.cutFillsTable || [];
    const dataRows = cutFillsTable.filter(
      (r: any) => r.key !== 'totals' && r.plotNo !== 'TOTALS' && r.plotNo !== 'Total'
    );
    const totalOnsite = dataRows.reduce((s: number, r: any) => s + (Number(r.topsoilDisposalOnsite) || 0), 0);
    const totalOffsite = dataRows.reduce((s: number, r: any) => s + (Number(r.topsoilDisposalOffsite) || 0), 0);
    const topsoilQty = Math.abs(totalOnsite - totalOffsite);

    const topsoilMgmt = groundworksData?.topsoilManagement;
    const topsoilCode = topsoilMgmt?.code?.trim().toUpperCase() || '';
    const topsoilIsExcluded = topsoilCode === 'NONE' || !topsoilCode;
    const topsoilBaseRate = topsoilIsExcluded ? 0 : (codeRateLookup.get(topsoilCode) ?? 0);
    const topsoilRate = topsoilBaseRate * mk;
    const topsoilRoundedQty = Math.round(topsoilQty);
    const topsoilDisplayRate = parseFloat(topsoilRate.toFixed(2));
    const topsoilAmount = topsoilIsExcluded ? 0 : parseFloat((topsoilRoundedQty * topsoilDisplayRate).toFixed(2));

    // --- 3.2 Subsoil ---
    const soilData = groundworksData?.soilManagementData;
    const soilRows = [
      { cutField: 'foundationsCutArisings', fillField: 'foundationsFill' },
      { cutField: 'slabPathsCut', fillField: 'slabPathsFill' },
      { cutField: 'gardensCut', fillField: 'gardensFill' },
      { cutField: 'drivesCut', fillField: 'drivesFill' },
      { cutField: 'foundationsArisingsCut', fillField: 'foundationsArisingsFill' },
      { cutField: 'plotDrainageCut', fillField: 'plotDrainageFill' },
      { cutField: 'wallsCut', fillField: 'wallsFill' },
      { cutField: 'plotServicesCut', fillField: 'plotServicesFill' },
    ];
    const subsoilTotalCut = soilRows.reduce((s, r) => s + (Number(soilData?.[r.cutField]) || 0), 0);
    const subsoilTotalFill = soilRows.reduce((s, r) => s + (Number(soilData?.[r.fillField]) || 0), 0);
    const subsoilQty = Math.abs(subsoilTotalCut - subsoilTotalFill);

    const subsoilMgmt = groundworksData?.subsoilManagement;
    const subsoilCode = subsoilMgmt?.code?.trim().toUpperCase() || '';
    const subsoilIsExcluded = subsoilCode === 'NONE' || !subsoilCode;
    const subsoilBaseRate = subsoilIsExcluded ? 0 : (codeRateLookup.get(subsoilCode) ?? 0);
    const subsoilRate = subsoilBaseRate * mk;
    const subsoilRoundedQty = Math.round(subsoilQty);
    const subsoilDisplayRate = parseFloat(subsoilRate.toFixed(2));
    const subsoilAmount = subsoilIsExcluded ? 0 : parseFloat((subsoilRoundedQty * subsoilDisplayRate).toFixed(2));

    return topsoilAmount + subsoilAmount;
  })();

  return base + soilTotal;
};

/**
 * Calculate Drainage grand total - matches DrainageBillSummary EXACTLY
 * Uses the same per-plot rate calculation and item-level logic as the UI component
 */
export const calculateDrainageGrandTotal = (
  groundworksData: any,
  _ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  markupFactor?: number
): number => {
  // Support both drainageTableStandard (primary) and drainageTable (legacy)
  const drainageRows: any[] = groundworksData?.drainageTableStandard || groundworksData?.drainageTable || [];

  if (drainageRows.length === 0) {
    return 0;
  }

  const osmaConfig = groundworksData?.drainageDimensionValues || {};
  const polypipeConfig = groundworksData?.drainagePolypipeConfig || {};

  // Build rate lookup from item library (same as DrainageBillSummary)
  const rateLookupMap = itemLibraryData
    ? createDrainageRateLookupMap(itemLibraryData, groundworksData)
    : new Map<string, any>();

  const sumField = (field: string): number =>
    drainageRows.reduce((acc: number, row: any) => acc + (parseFloat(row[field]) || 0), 0);

  const getRate = (fieldName: string): number =>
    (rateLookupMap.get(fieldName)?.rate || 0) * (markupFactor ?? 1);

  // Match DrainageBillSummary.buildPerPlotRow EXACTLY (with markup applied at rate level):
  //   combinedSum = Σ_field (sumField(field) × rate(field)) over all combined fields
  //   rate        = combinedSum / nrPlotsTakeOff
  //   amount      = Math.round(totalPlots) × parseFloat(rate.toFixed(2))
  const calcPerPlotAmount = (combinedFields: readonly string[], totalPlots: number, nrPlotsTakeOff: number): number => {
    const combinedSum = combinedFields.reduce((sum, field) => sum + sumField(field) * getRate(field), 0);
    const rate = nrPlotsTakeOff > 0 ? combinedSum / nrPlotsTakeOff : 0;
    const roundedPlots = Math.round(totalPlots);
    return parseFloat((roundedPlots * parseFloat(rate.toFixed(2))).toFixed(2));
  };

  const calcItemAmount = (fieldName: string, quantityOverride?: number): number => {
    const quantity = quantityOverride !== undefined ? quantityOverride : sumField(fieldName);
    const rate = getRate(fieldName);
    const roundedQty = Math.round(quantity);
    return parseFloat((roundedQty * parseFloat(rate.toFixed(2))).toFixed(2));
  };

  const hasOsma = drainageRows.some((row: any) => (parseFloat(row.osma32mmBend) || 0) > 0 || (parseFloat(row.osmaStorm32mmBend) || 0) > 0);
  const hasPolypipe = drainageRows.some((row: any) => (parseFloat(row.polypipe32mmBend) || 0) > 0 || (parseFloat(row.polypipeStorm32mmBend) || 0) > 0);
  const usePolypipe = hasPolypipe && !hasOsma;

  let total = 0;

  if (usePolypipe) {
    const ppTotalPlots = parseFloat(polypipeConfig.totalPlots) || 0;
    const ppNrPlotsTakeOff = parseFloat(polypipeConfig.nrPlotsTakeOff) || 0;

    // Polypipe Foul
    total += calcPerPlotAmount(POLYPIPE_FOUL_COMBINED_FIELDS, ppTotalPlots, ppNrPlotsTakeOff);
    total += calcItemAmount("polypipeNmePpic");
    total += calcItemAmount("polypipe600mmNmePpic");

    // Polypipe Storm
    total += calcPerPlotAmount(POLYPIPE_STORM_COMBINED_FIELDS, ppTotalPlots, ppNrPlotsTakeOff);
    total += calcItemAmount("polypipeStormNmePpic");
    total += calcItemAmount("polypipeStorm600mmNmePpic");
    total += calcItemAmount("polypipeStormYardGully", sumField("polypipeStormYardGully") || sumField("yardGully"));
    total += calcItemAmount("polypipeStormDrainageChannel", sumField("polypipeStormDrainageChannel") || sumField("drainageChannel"));
    total += calcItemAmount("polypipeStormLandDrain", sumField("polypipeStormLandDrain") || sumField("landDrain"));
  } else {
    const osmaTotalPlots = parseFloat(osmaConfig.foulTotalPlots) || 0;
    const osmaNrPlotsTakeOff = parseFloat(osmaConfig.foulNrPlotsTakeOff) || 0;

    // OSMA Foul
    total += calcPerPlotAmount(OSMA_FOUL_COMBINED_FIELDS, osmaTotalPlots, osmaNrPlotsTakeOff);
    total += calcItemAmount("osmaNmePpic");
    total += calcItemAmount("osma600mmNmePpic");

    // OSMA Storm
    total += calcPerPlotAmount(OSMA_STORM_COMBINED_FIELDS, osmaTotalPlots, osmaNrPlotsTakeOff);
    total += calcItemAmount("osmaStormNmePpic");
    total += calcItemAmount("osmaStorm600mmNmePpic");
    total += calcItemAmount("osmaStormYardGully", sumField("osmaStormYardGully") || sumField("yardGully"));
    total += calcItemAmount("osmaStormDrainageChannel", sumField("osmaStormDrainageChannel") || sumField("drainageChannel"));
    total += calcItemAmount("osmaStormLandDrain", sumField("osmaStormLandDrain") || sumField("landDrain"));
  }

  return total;
};

/**
 * Calculate Drainage Foul section total - matches DrainageBillSummary FOUL section EXACTLY
 */
export const calculateDrainageFoulTotal = (
  groundworksData: any,
  _ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse
): number => {
  const drainageRows: any[] = groundworksData?.drainageTableStandard || groundworksData?.drainageTable || [];
  if (drainageRows.length === 0) return 0;

  const osmaConfig = groundworksData?.drainageDimensionValues || {};
  const polypipeConfig = groundworksData?.drainagePolypipeConfig || {};

  const rateLookupMap = itemLibraryData
    ? createDrainageRateLookupMap(itemLibraryData, groundworksData)
    : new Map<string, any>();

  const sumField = (field: string): number =>
    drainageRows.reduce((acc: number, row: any) => acc + (parseFloat(row[field]) || 0), 0);

  const getRate = (fieldName: string): number =>
    rateLookupMap.get(fieldName)?.rate || 0;

  // Match DrainageBillSummary.buildPerPlotRow EXACTLY:
  //   combinedSum = Σ_field (sumField(field) × rate(field)) over all combined fields
  //   rate        = combinedSum / nrPlotsTakeOff
  //   amount      = Math.round(totalPlots) × parseFloat(rate.toFixed(2))
  const calcPerPlotAmount = (combinedFields: readonly string[], totalPlots: number, nrPlotsTakeOff: number): number => {
    const combinedSum = combinedFields.reduce((sum, field) => sum + sumField(field) * getRate(field), 0);
    const rate = nrPlotsTakeOff > 0 ? combinedSum / nrPlotsTakeOff : 0;
    const roundedPlots = Math.round(totalPlots);
    return parseFloat((roundedPlots * parseFloat(rate.toFixed(2))).toFixed(2));
  };

  const calcItemAmount = (fieldName: string): number => {
    const quantity = sumField(fieldName);
    const rate = getRate(fieldName);
    const roundedQty = Math.round(quantity);
    return parseFloat((roundedQty * parseFloat(rate.toFixed(2))).toFixed(2));
  };

  const hasOsma = drainageRows.some((row: any) => (parseFloat(row.osma32mmBend) || 0) > 0 || (parseFloat(row.osmaStorm32mmBend) || 0) > 0);
  const hasPolypipe = drainageRows.some((row: any) => (parseFloat(row.polypipe32mmBend) || 0) > 0 || (parseFloat(row.polypipeStorm32mmBend) || 0) > 0);
  const usePolypipe = hasPolypipe && !hasOsma;

  if (usePolypipe) {
    const ppTotalPlots = parseFloat(polypipeConfig.totalPlots) || 0;
    const ppNrPlotsTakeOff = parseFloat(polypipeConfig.nrPlotsTakeOff) || 0;
    return calcPerPlotAmount(POLYPIPE_FOUL_COMBINED_FIELDS, ppTotalPlots, ppNrPlotsTakeOff)
      + calcItemAmount("polypipeNmePpic")
      + calcItemAmount("polypipe600mmNmePpic");
  } else {
    const osmaTotalPlots = parseFloat(osmaConfig.foulTotalPlots) || 0;
    const osmaNrPlotsTakeOff = parseFloat(osmaConfig.foulNrPlotsTakeOff) || 0;
    return calcPerPlotAmount(OSMA_FOUL_COMBINED_FIELDS, osmaTotalPlots, osmaNrPlotsTakeOff)
      + calcItemAmount("osmaNmePpic")
      + calcItemAmount("osma600mmNmePpic");
  }
};

/**
 * Calculate Drainage Storm section total - matches DrainageBillSummary STORM section EXACTLY
 */
export const calculateDrainageStormTotal = (
  groundworksData: any,
  _ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse
): number => {
  const drainageRows: any[] = groundworksData?.drainageTableStandard || groundworksData?.drainageTable || [];
  if (drainageRows.length === 0) return 0;

  const osmaConfig = groundworksData?.drainageDimensionValues || {};
  const polypipeConfig = groundworksData?.drainagePolypipeConfig || {};

  const rateLookupMap = itemLibraryData
    ? createDrainageRateLookupMap(itemLibraryData, groundworksData)
    : new Map<string, any>();

  const sumField = (field: string): number =>
    drainageRows.reduce((acc: number, row: any) => acc + (parseFloat(row[field]) || 0), 0);

  const getRate = (fieldName: string): number =>
    rateLookupMap.get(fieldName)?.rate || 0;

  // Match DrainageBillSummary.buildPerPlotRow EXACTLY:
  //   combinedSum = Σ_field (sumField(field) × rate(field)) over all combined fields
  //   rate        = combinedSum / nrPlotsTakeOff
  //   amount      = Math.round(totalPlots) × parseFloat(rate.toFixed(2))
  const calcPerPlotAmount = (combinedFields: readonly string[], totalPlots: number, nrPlotsTakeOff: number): number => {
    const combinedSum = combinedFields.reduce((sum, field) => sum + sumField(field) * getRate(field), 0);
    const rate = nrPlotsTakeOff > 0 ? combinedSum / nrPlotsTakeOff : 0;
    const roundedPlots = Math.round(totalPlots);
    return parseFloat((roundedPlots * parseFloat(rate.toFixed(2))).toFixed(2));
  };

  const calcItemAmount = (fieldName: string, quantityOverride?: number): number => {
    const quantity = quantityOverride !== undefined ? quantityOverride : sumField(fieldName);
    const rate = getRate(fieldName);
    const roundedQty = Math.round(quantity);
    return parseFloat((roundedQty * parseFloat(rate.toFixed(2))).toFixed(2));
  };

  const hasOsma = drainageRows.some((row: any) => (parseFloat(row.osma32mmBend) || 0) > 0 || (parseFloat(row.osmaStorm32mmBend) || 0) > 0);
  const hasPolypipe = drainageRows.some((row: any) => (parseFloat(row.polypipe32mmBend) || 0) > 0 || (parseFloat(row.polypipeStorm32mmBend) || 0) > 0);
  const usePolypipe = hasPolypipe && !hasOsma;

  if (usePolypipe) {
    const ppTotalPlots = parseFloat(polypipeConfig.totalPlots) || 0;
    const ppNrPlotsTakeOff = parseFloat(polypipeConfig.nrPlotsTakeOff) || 0;
    return calcPerPlotAmount(POLYPIPE_STORM_COMBINED_FIELDS, ppTotalPlots, ppNrPlotsTakeOff)
      + calcItemAmount("polypipeStormNmePpic")
      + calcItemAmount("polypipeStorm600mmNmePpic")
      + calcItemAmount("polypipeStormYardGully", sumField("polypipeStormYardGully") || sumField("yardGully"))
      + calcItemAmount("polypipeStormDrainageChannel", sumField("polypipeStormDrainageChannel") || sumField("drainageChannel"))
      + calcItemAmount("polypipeStormLandDrain", sumField("polypipeStormLandDrain") || sumField("landDrain"));
  } else {
    const osmaTotalPlots = parseFloat(osmaConfig.foulTotalPlots) || 0;
    const osmaNrPlotsTakeOff = parseFloat(osmaConfig.foulNrPlotsTakeOff) || 0;
    return calcPerPlotAmount(OSMA_STORM_COMBINED_FIELDS, osmaTotalPlots, osmaNrPlotsTakeOff)
      + calcItemAmount("osmaStormNmePpic")
      + calcItemAmount("osmaStorm600mmNmePpic")
      + calcItemAmount("osmaStormYardGully", sumField("osmaStormYardGully") || sumField("yardGully"))
      + calcItemAmount("osmaStormDrainageChannel", sumField("osmaStormDrainageChannel") || sumField("drainageChannel"))
      + calcItemAmount("osmaStormLandDrain", sumField("osmaStormLandDrain") || sumField("landDrain"));
  }
};

/**
 * Calculate Plot Services section total only (excludes Service Connections)
 */
export const calculatePlotServicesSectionTotal = (
  groundworksData: any,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse
): number => {
  if (!groundworksData?.plotServiceTable || groundworksData.plotServiceTable.length === 0) return 0;
  return calculateBOQGrandTotal(
    groundworksData,
    "Plot Service",
    {
      'Plot Services': [
        'plots', 'apartments', 'eo25mmBarrierPipe', 'eo32mmBarrierPipe',
        'virginMedia', 'garageDuct', 'privateStLightDuct', 'pccEdgingsGasBoxes',
        'ductingToChargingPoint', 'concBaseChargingPoints', 'installBollardBase', 'airSourceHeatPump',
      ],
    },
    undefined,
    ratesMapping,
    undefined,
    itemLibraryData,
    applyPlotServiceRatesToSummary
  );
};

/**
 * Calculate Plot Drives grand total - matches PlotDrivesBillSummary EXACTLY
 */
export const calculatePlotDrivesGrandTotal = (
  groundworksData: any,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  markupFactor?: number
): number => {
  if (
    !groundworksData?.plotDrivesTable ||
    groundworksData.plotDrivesTable.length === 0
  ) {
    return 0;
  }
  const dimensionValues = groundworksData?.plotDrivesDimensionValues;
  const columnHeaders = groundworksData?.plotDrivesColumnHeaders;
  return calculateBOQGrandTotal(
    groundworksData,
    "Plot Drives",
    PLOT_DRIVES_CATEGORIES,
    dimensionValues,
    ratesMapping,
    columnHeaders,
    itemLibraryData,
    applyPlotDrivesRatesToSummary,
    markupFactor
  );
};

/**
 * Calculate Plot Service grand total - matches PlotServiceBillSummary EXACTLY
 *
 * PlotServiceBillSummary rounds the BASE rate first (parseFloat(rate.toFixed(2))),
 * then multiplies by markupFactor without a second round. This is different from
 * other summaries (which multiply then round), so it needs its own implementation.
 */
export const calculatePlotServiceGrandTotal = (
  groundworksData: any,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  markupFactor?: number
): number => {
  if (
    !groundworksData?.plotServiceTable ||
    groundworksData.plotServiceTable.length === 0
  ) {
    return 0;
  }

  const mk = markupFactor ?? 1;

  const PLOT_SERVICE_FULL: Record<string, string[]> = {
    'Plot Services': [
      'plots', 'apartments', 'eo25mmBarrierPipe', 'eo32mmBarrierPipe',
      'virginMedia', 'garageDuct', 'privateStLightDuct', 'pccEdgingsGasBoxes',
      'ductingToChargingPoint', 'concBaseChargingPoints', 'installBollardBase', 'airSourceHeatPump',
    ],
    'Service Connections': [
      'serviceConnectionPlots', 'serviceConnectionApartments', 'vacEx',
    ],
  };

  let items = generateBOQSummaryWithCategories(
    groundworksData,
    "Plot Service",
    PLOT_SERVICE_FULL,
    undefined,
    ratesMapping
  );

  if (itemLibraryData) {
    const rateLookupMap = createPlotServiceRateLookupMap(itemLibraryData);
    items = applyPlotServiceRatesToSummary(items, rateLookupMap);
  }

  const config = groundworksData?.plotServiceConfig as {
    plotServicesApartmentsMultiple?: number;
    serviceConnectionApartmentsMultiple?: number;
  } | undefined;
  const plotMultiple = config?.plotServicesApartmentsMultiple ?? 1;
  const scMultiple = config?.serviceConnectionApartmentsMultiple ?? 1;

  // Mirror PlotServiceBillSummary rawProcessedData exactly:
  // 1. Round base rate to 2dp
  // 2. Apply apartments multiple to rounded rate, round again (for apartments fields only)
  // 3. markedUpRate = effectiveRate * markupFactor (no rounding after markup)
  // 4. amount = parseFloat(Math.abs(qty * markedUpRate).toFixed(2))  — round each row
  // 5. total = sum of per-row amounts
  return items.reduce((sum: number, item: any) => {
    const roundedQty = Math.round(item.quantity || 0);
    const baseRate = parseFloat(((item.rate || 0) as number).toFixed(2));
    let effectiveRate = baseRate;
    if (item.fieldName === 'apartments' && plotMultiple > 1) {
      effectiveRate = parseFloat((baseRate * plotMultiple).toFixed(2));
    } else if (item.fieldName === 'serviceConnectionApartments' && scMultiple > 1) {
      effectiveRate = parseFloat((baseRate * scMultiple).toFixed(2));
    }
    const rowAmount = parseFloat(Math.abs(roundedQty * (effectiveRate * mk)).toFixed(2));
    return sum + rowAmount;
  }, 0);
};

export const calculateServiceConnectionsGrandTotal = (
  groundworksData: any,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse
): number => {
  if (
    !groundworksData?.plotServiceTable ||
    groundworksData.plotServiceTable.length === 0
  ) {
    return 0;
  }
  // Use full field list matching PlotServiceBillSummary UI
  // Step name must be "Plot Service" so boqSummaryGenerator resolves to plotServiceTable
  const SERVICE_CONNECTIONS_FULL: Record<string, string[]> = {
    'Service Connections': ['serviceConnectionPlots', 'serviceConnectionApartments', 'vacEx'],
  };
  return calculateBOQGrandTotal(
    groundworksData,
    "Plot Service",
    SERVICE_CONNECTIONS_FULL,
    undefined,
    ratesMapping,
    undefined,
    itemLibraryData,
    applyPlotServiceRatesToSummary
  );
};

/**
 * Calculate Plot Pavings grand total - matches PlotPavingsBillSummary EXACTLY
 */
export const calculatePlotPavingsGrandTotal = (
  groundworksData: any,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  markupFactor?: number
): number => {
  if (
    !groundworksData?.plotPavingsTable ||
    groundworksData.plotPavingsTable.length === 0
  ) {
    return 0;
  }
  const dimensionValues = groundworksData?.plotPavingsDimensionValues;
  const columnHeaders = reconstructPlotPavingsColumnHeaders(
    groundworksData?.plotPavingsColumnHeaders || {},
    dimensionValues || {}
  );
  return calculateBOQGrandTotal(
    groundworksData,
    "Plot Pavings",
    PLOT_PAVINGS_CATEGORIES,
    dimensionValues,
    ratesMapping,
    columnHeaders,
    itemLibraryData,
    applyPlotPavingsRatesToSummary,
    markupFactor
  );
};

/**
 * Calculate Scaffold grand total - matches ScaffoldBillSummary EXACTLY.
 *
 * ScaffoldBillSummary consolidates each category (Scaffold Platforms / Loading Bays /
 * Stair Access) into one line whose amount = sum of per-breakdown-row amounts, then the
 * displayed consolidated amount = Math.abs(Math.round(platformQty) × parseFloat(combinedRate.toFixed(2))).
 * The standard calculateBOQGrandTotal path processes each field independently and produces
 * different results, so we replicate the consolidation here.
 */
export const calculateScaffoldGrandTotal = (
  groundworksData: any,
  _ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  markupFactor?: number
): number => {
  if (
    !groundworksData?.scaffoldTable ||
    groundworksData.scaffoldTable.length === 0
  ) {
    return 0;
  }
  if (!itemLibraryData) return 0;

  // Mirror of CONSOLIDATE_FIELDS in ScaffoldBillSummary. Rate = sum of the
  // Level & Compact / Crush 6F2 / Type1 rates, plus the Geotextile Layer rate
  // ONLY when that section's checkbox (scaffoldDimensionValues[geotextileEnabledKey]
  // === "true") is on.
  const SCAFFOLD_CONSOLIDATE: Record<string, { quantityField: string; rateFields: string[]; geotextileField: string; geotextileEnabledKey: string }> = {
    'Scaffold Platforms': {
      quantityField: 'scaffoldPlatform',
      rateFields: ['scaffoldLevelCompact', 'scaffoldGeotextileLayer', 'scaffoldCrush6F2', 'scaffoldType1'],
      geotextileField: 'scaffoldGeotextileLayer',
      geotextileEnabledKey: 'scaffoldGeotextileEnabled',
    },
    'Loading Bays': {
      quantityField: 'loadingBay4x2m',
      rateFields: ['loadingLevelCompact', 'loadingGeotextileLayer', 'loadingCrush6F2', 'loadingType1'],
      geotextileField: 'loadingGeotextileLayer',
      geotextileEnabledKey: 'loadingGeotextileEnabled',
    },
    'Stair Access': {
      quantityField: 'stairAccess3x3m',
      rateFields: ['stairLevelCompact', 'stairGeotextileLayer', 'stairCrush6F2', 'stairType1'],
      geotextileField: 'stairGeotextileLayer',
      geotextileEnabledKey: 'stairGeotextileEnabled',
    },
  };

  const dimensionValues: Record<string, string> = groundworksData.scaffoldDimensionValues || {};

  const baseItems = generateBOQSummaryWithCategories(
    groundworksData,
    'Scaffold',
    SCAFFOLD_CATEGORIES,
    undefined,
    null,
  );
  const rateLookupMap = createScaffoldRateLookupMap(itemLibraryData, groundworksData);
  const itemsWithRates = applyScaffoldRatesToSummary(baseItems, rateLookupMap);

  let grandTotal = 0;

  Object.entries(SCAFFOLD_CONSOLIDATE).forEach(([category, config]) => {
    const platformItem = itemsWithRates.find(
      (i: any) => i.fieldName === config.quantityField && i.category === category
    );
    if (!platformItem) return;

    const platformQty: number = platformItem.quantity || 0;
    const geotextileEnabled = dimensionValues[config.geotextileEnabledKey] === 'true';

    // Sum each field's marked-up rate AFTER rounding it to 2dp — matches the
    // Rate Breakdown modal's Final Rate exactly (ScaffoldBillSummary.tsx).
    const combinedRate = config.rateFields.reduce((sum, fieldName) => {
      const rateItem = itemsWithRates.find(
        (i: any) => i.fieldName === fieldName && i.category === category
      );
      const fallbackRate = rateLookupMap.get(fieldName)?.rate ?? 0;
      const rate: number = rateItem?.rate ?? fallbackRate;
      if (fieldName === config.geotextileField && !geotextileEnabled) return sum;
      return sum + parseFloat((rate * (markupFactor ?? 1)).toFixed(2));
    }, 0);

    const consolidatedAmount = Math.abs(Math.round(platformQty) * combinedRate);
    grandTotal += consolidatedAmount;
  });

  return grandTotal;
};

/**
 * Calculate Garden grand total - matches GardenBillSummary EXACTLY
 */
export const calculateGardenGrandTotal = (
  groundworksData: any,
  ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  markupFactor?: number
): number => {
  if (
    !groundworksData?.gardenTable ||
    groundworksData.gardenTable.length === 0
  ) {
    return 0;
  }
  const dimensionValues = groundworksData?.gardenDimensionValues;
  const columnHeaders = groundworksData?.gardenColumnHeaders;
  return calculateBOQGrandTotal(
    groundworksData,
    "Garden",
    GARDEN_CATEGORIES,
    dimensionValues,
    ratesMapping,
    columnHeaders,
    itemLibraryData,
    applyGardenRatesToSummary,
    markupFactor
  );
};

/**
 * Calculate Ret & Screen Walls grand total - matches RetScreenWallsBillSummary EXACTLY
 */
export const calculateRetScreenWallsGrandTotal = (
  groundworksData: any,
  _ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  persistedSpecs?: RetainingWallsBuildUpSpecification[],
  markupFactor?: number
): number => {
  if (
    !groundworksData?.retainingWallsTable ||
    groundworksData.retainingWallsTable.length === 0
  ) {
    return 0;
  }

  // Build evaluateCode -> nettRate map from item library (flat array)
  const rateMap = new Map<string, number>();
  const libItems = itemLibraryData as unknown as any[];
  if (Array.isArray(libItems)) {
    libItems.forEach((lib: any) => {
      const code = lib.resourceCode?.trim().toUpperCase();
      if (code) rateMap.set(code, lib.nettRate ?? 0);
    });
  }

  const retainingWallsData = groundworksData.retainingWallsTable as any[];

  const mk = markupFactor ?? 1;

  // Screen Walls (5 standalone fields from the take-off table) — priced as
  // `qty × library rate × markupFactor`. Mirrors RetScreenWallsBillSummary.
  // Screen Walls / EO Pillar dropdowns drive the evaluate code (Free Issue:
  // GWALPGW003/GWALPGW003A vs M&J Supply: GWALPGW003B/GWALPGW003C) for the
  // `screenWalls`/`screenWallsEO` fields, each independently, per the
  // Configure modal.
  const screenWallsLabel: string | undefined = groundworksData?.wallsBuildUpConfig?.screenWallsLabel;
  const eoPillarLabel: string | undefined = groundworksData?.wallsBuildUpConfig?.eoPillarLabel;
  let screenWallsTotal = 0;
  SCREEN_WALL_BOQ_FIELDS.forEach((field) => {
    const qty = retainingWallsData.reduce(
      (sum: number, row: any) => sum + (Number(row[field.id]) || 0),
      0,
    );
    if (qty <= 0) return;
    const baseRate = rateMap.get(getScreenWallFieldCode(field, screenWallsLabel, eoPillarLabel).trim().toUpperCase()) || 0;
    const markedUpRate = baseRate * mk;
    const roundedQty = Math.round(qty);
    screenWallsTotal += parseFloat((roundedQty * parseFloat(markedUpRate.toFixed(2))).toFixed(2));
  });

  // Total metres per wall spec from take-off table
  const wallTypeTotals: Record<string, number> = {};
  retainingWallsData.forEach((row: any) => {
    wallSpecifications.forEach((spec) => {
      wallTypeTotals[spec.id] = (wallTypeTotals[spec.id] || 0) + ((row[spec.id] as number) || 0);
    });
  });

  // Build spec items map from persisted API data when available
  const persistedSpecMap = new Map<string, RetainingWallsBuildUpSpecification>();
  if (persistedSpecs) {
    persistedSpecs.forEach((s) => persistedSpecMap.set(s.id, s));
  }

  // Build flat description → mock ID map (same as WallsSpecificationContainer)
  const descriptionToMockId = new Map<string, string>();
  wallSpecifications.forEach((spec) =>
    spec.items.forEach((mi) => descriptionToMockId.set(mi.description.toLowerCase(), mi.id))
  );

  // Facing Brickwork dropdown drives the evaluate code (GWALPGW0020 vs
  // GWALPGW0021) for `facing_bricks_*_wide` rows so Sum Analysis tracks the
  // variant picked in the Configure modal.
  const facingBrickworkLabel: string | undefined = groundworksData?.wallsBuildUpConfig?.facingBrickworkLabel;

  // Net total = Σ_specs (netPerM × wallQtyM)
  let total = 0;
  wallSpecifications.forEach((spec) => {
    const wallQtyM = wallTypeTotals[spec.id] || 0;
    if (wallQtyM === 0) return;

    // Match RetScreenWallsBillSummary: API persisted specs are the single
    // source of truth. No fallback to local-state `wallsBuildUpDimensions` —
    // that fallback caused Sum Analysis and the Bill Summary to disagree
    // when nothing had been persisted yet via WallsSpecificationContainer,
    // and broke Plot Matrix ↔ BoQ reconciliation when `mergePersistedWallBuildUp`
    // left stale local-state dims in place.
    const persistedSpec = persistedSpecMap.get(spec.id);
    const rawItems: Array<{ description: string; net: number; rate?: number }> =
      persistedSpec?.items ?? [];

    // Filter same as RetScreenWallsBillSummary
    const filteredItems = rawItems.filter((item) => {
      const desc = String(item.description ?? "").trim();
      return desc.length > 0 && !/^[0-9.]+$/.test(desc);
    });

    const savedItems: Array<{ id: string; rate: number }> =
      (groundworksData?.wallsBuildUpData?.[spec.id] || []) as Array<{ id: string; rate: number }>;

    let netPerM = 0;
    filteredItems.forEach((item) => {
      const descKey = (item.description || '').toLowerCase();
      const mockId = descriptionToMockId.get(descKey);
      const persistedId = (item as any).id || '';
      const derivedId =
        persistedId || (mockId ?? descKey.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""));
      // Facing Brickwork — code is resolved from the Configure dropdown
      // (`facingBrickworkLabel` → GWALPGW0020 / GWALPGW0021), and the
      // effective rate is `libraryRate × N` where N is the half-brick width
      // count (0.5 br wide → ×1, 1 br wide → ×2, …, 3.5 br wide → ×7).
      // 100mm-blockwork rows use the same `libraryRate × N` pattern with N
      // = course count. Stale persisted snapshots are ignored — see
      // `WALLS_BUILD_UP_RATE_MULTIPLIERS`.
      const isFacingBrick = isFacingBrickworkItem(derivedId);
      const evaluateCode = isFacingBrick
        ? (resolveWallsBuildUpItemCode(derivedId, facingBrickworkLabel) || '')
        : (WALLS_BUILD_UP_ITEM_CODE_MAP[derivedId] || '');
      const libraryRate = evaluateCode ? (rateMap.get(evaluateCode.trim().toUpperCase()) ?? 0) : 0;
      const savedItem = savedItems.find((s) => s.id === derivedId);
      const isMultiplied = isBlockworkBuildUpItem(derivedId);
      const rateMultiplier = getWallsBuildUpRateMultiplier(derivedId);
      const baseRate = isMultiplied
        ? libraryRate * rateMultiplier
        : (savedItem && savedItem.rate !== 0 ? savedItem.rate : libraryRate);
      netPerM += Math.abs(item.net * baseRate);
    });

    const roundedWallQtyM = Math.round(wallQtyM);
    const ratePerM = netPerM * mk;
    const rowAmount = roundedWallQtyM * parseFloat(ratePerM.toFixed(2));
    total += parseFloat(rowAmount.toFixed(2));
  });

  return total + screenWallsTotal;
};

/**
 * Calculate Geowall & Tobermore Walls grand total - matches GEOToberWallBillSummary EXACTLY
 */
export const calculateGEOToberWallGrandTotal = (
  groundworksData: any,
  _ratesMapping?: RateMappingJSON | null,
  itemLibraryData?: ItemLibraryResponse,
  persistedSpecs?: GeoToberWallBuildUpSpecification[],
  markupFactor?: number
): number => {
  if (
    !groundworksData?.geoToberWallTable ||
    groundworksData.geoToberWallTable.length === 0
  ) {
    return 0;
  }

  // Build evaluateCode -> nettRate map from item library (flat array)
  const rateMap = new Map<string, number>();
  const libItems = itemLibraryData as unknown as any[];
  if (Array.isArray(libItems)) {
    libItems.forEach((lib: any) => {
      const code = lib.resourceCode?.trim().toUpperCase();
      if (code) rateMap.set(code, lib.nettRate ?? 0);
    });
  }

  const tableData = groundworksData.geoToberWallTable as any[];

  // Total metres per spec from take-off table
  const wallTypeTotals: Record<string, number> = {};
  tableData.forEach((row: any) => {
    Object.entries(geoToberWallFieldToSpecId).forEach(([fieldName, specId]) => {
      wallTypeTotals[specId] = (wallTypeTotals[specId] || 0) + ((row[fieldName] as number) || 0);
    });
  });

  // Build spec items map from persisted API data when available
  const persistedSpecMap = new Map<string, GeoToberWallBuildUpSpecification>();
  if (persistedSpecs) {
    persistedSpecs.forEach((s) => persistedSpecMap.set(s.id, s));
  }

  // Net total = Σ_specs (netPerM × wallQtyM)
  let total = 0;
  geoToberWallSpecifications.forEach((spec) => {
    const wallQtyM = wallTypeTotals[spec.id] || 0;
    if (wallQtyM === 0) return;

    const codeMap =
      spec.type === "geowall"
        ? GEO_WALL_BUILD_UP_ITEM_CODE_MAP
        : TOBERMORE_BUILD_UP_ITEM_CODE_MAP;

    // Build per-type description → mock ID map (same as GeoToberWallSpecificationContainer)
    const descriptionToMockId = new Map<string, string>();
    geoToberWallSpecifications.forEach((s) => {
      if (s.type === spec.type) {
        s.items.forEach((mi) => descriptionToMockId.set(mi.description.toLowerCase(), mi.id));
      }
    });

    // Match GEOToberWallBillSummary: only use persisted (API) items. No fallback
    // to local `geoToberWallBuildUpDimensions` — that fallback caused Sum
    // Analysis to show an amount for Geowall & Tobermore Walls even when the
    // Bill Summary itself rendered no line items (because nothing had been
    // persisted yet via WallsSpecificationContainer).
    const persistedSpec = persistedSpecMap.get(spec.id);
    const rawItems: Array<{ description: string; net: number; rate?: number }> =
      persistedSpec?.items ?? [];

    // Filter same as GEOToberWallBillSummary
    const filteredItems = rawItems.filter((item) => {
      const desc = String(item.description ?? "").trim();
      return desc.length > 0 && !/^[0-9.]+$/.test(desc);
    });

    const savedItems: Array<{ id: string; rate: number }> =
      (groundworksData?.geoToberWallBuildUpData?.[spec.id] || []) as Array<{ id: string; rate: number }>;

    let netPerM = 0;
    filteredItems.forEach((item) => {
      const descKey = (item.description || '').toLowerCase();
      const mockId = descriptionToMockId.get(descKey);
      const derivedId = mockId ?? descKey.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
      const evaluateCode = codeMap[derivedId] || '';
      const libraryRate = evaluateCode ? (rateMap.get(evaluateCode.trim().toUpperCase()) ?? 0) : 0;
      const savedItem = savedItems.find((s) => s.id === derivedId);
      const baseRate = savedItem && savedItem.rate !== 0 ? savedItem.rate : libraryRate;
      netPerM += Math.abs(item.net * baseRate);
    });

    const roundedWallQtyM = Math.round(wallQtyM);
    const ratePerM = netPerM * (markupFactor ?? 1);
    const rowAmount = roundedWallQtyM * parseFloat(ratePerM.toFixed(2));
    total += parseFloat(rowAmount.toFixed(2));
  });

  return total;
};

// Legacy functions - keeping for backwards compatibility
export const calculateStepTotal = calculateStepGrandTotal;

export const calculateStepTotalWithCategories = (
  groundworksData: any,
  stepName: string,
  categories: { [categoryName: string]: string[] }
): number => {
  return calculateStepGrandTotal(groundworksData, stepName, categories);
};

/**
 * Standard sections used for groundworks total calculation
 * Single source of truth - used everywhere totals are calculated
 */
export const GROUNDWORKS_SECTIONS = [
  { name: "Cut & Fills", stepName: "Cut & Fills" },
  { name: "Foundations", stepName: "Foundations" },
  { name: "Scaffold Platforms", stepName: "Scaffold" },
  { name: "Drainage", stepName: "Drainage" },
  { name: "Plot Service", stepName: "Plot Service" },
  { name: "Plot Drives", stepName: "Plot Drives" },
  { name: "Ret & Screen Walls", stepName: "Ret & Screen Walls" },
  { name: "Service Connections", stepName: "Service Connections" },
  { name: "Plot Paving", stepName: "Plot Pavings" },
  { name: "Garden", stepName: "Garden" },
];

/**
 * STEP_CATEGORIES mapping for all groundworks sections
 * Single source of truth for category mappings
 */
export const STEP_CATEGORIES: {
  [key: string]: { [categoryName: string]: string[] };
} = {
  "Cut & Fills": CUT_FILL_CATEGORIES,
  Foundations: FOUNDATION_CATEGORIES,
  Scaffold: SCAFFOLD_CATEGORIES,
  Drainage: DRAINAGE_CATEGORIES,
  "Plot Service": PLOT_SERVICE_CATEGORIES,
  "Plot Drives": PLOT_DRIVES_CATEGORIES,
  "Ret & Screen Walls": RET_SCREEN_WALLS_CATEGORIES,
  "Service Connections": SERVICE_CONNECTIONS_CATEGORIES,
  "Plot Pavings": PLOT_PAVINGS_CATEGORIES,
  Garden: GARDEN_CATEGORIES,
  // "Geowall & Tobermore Walls": GEO_TOBER_WALL_CATEGORIES, // Excluded - not required
};

/**
 * Calculate total from all groundworks sections
 * This is the SINGLE SOURCE OF TRUTH for groundworks total calculation
 * @param groundworksData - The groundworks data from tender state
 * @param ratesMapping - Optional rate mapping for rate card calculations
 * @returns The total of all groundworks sections
 */
export const calculateAllGroundworksSectionsTotal = (
  groundworksData: any,
  ratesMapping?: RateMappingJSON | null
): number => {
  if (!groundworksData) return 0;

  let total = 0;
  GROUNDWORKS_SECTIONS.forEach((section) => {
    const categories = STEP_CATEGORIES[section.stepName];
    const sectionTotal = calculateStepGrandTotal(
      groundworksData,
      section.stepName,
      categories || undefined,
      ratesMapping
    );
    if (sectionTotal > 0) {
      total += sectionTotal;
    }
  });

  return total;
};

/**
 * Calculate combined tender total based on job types
 * This is the SINGLE SOURCE OF TRUTH for tender total calculation
 * @param groundworksTotal - The groundworks grand total
 * @param roadsAndSewersTotal - The roads and sewers total (if applicable)
 * @param jobTypes - The selected job types
 * @returns The combined tender total
 */
export const calculateTenderTotal = (
  groundworksTotal: number,
  roadsAndSewersTotal: number,
  _jobTypes?: { roadsAndSewers?: boolean; groundworks?: boolean }
): number => {
  // Tender total is always Groundworks + Roads & Sewers, regardless of which
  // job-type flags are set (an absent domain contributes 0).
  return (groundworksTotal || 0) + (roadsAndSewersTotal || 0);
};

/**
 * Get rate card from localStorage (raw data only)
 * @returns Object with rateCard
 */
export const getRateCardFromLocalStorage = (): {
  rateCard: null;
} => {
  return { rateCard: null };
};

/**
 * Calculate all totals for tender submission
 * This is the SINGLE SOURCE OF TRUTH for all totals used in API payloads
 * @param tenderState - The full tender state
 * @param ratesMapping - Optional rate mapping (will fetch from localStorage if not provided)
 * @returns Object with groundworksTotal, roadsAndSewersTotal, and tenderTotal
 */
export const calculateAllTenderTotals = (
  tenderState: any,
  ratesMapping?: RateMappingJSON | null
): {
  groundworksTotal: number;
  roadsAndSewersTotal: number;
  tenderTotal: number;
} => {
  const finalRatesMapping = ratesMapping ?? null;

  // Calculate groundworks total from all sections
  const groundworksTotal = calculateAllGroundworksSectionsTotal(
    tenderState.groundworks,
    finalRatesMapping
  );

  // Get R&S total only if that job type is selected
  const hasRoadsAndSewers = tenderState.jobTypes?.roadsAndSewers === true;
  const roadsAndSewersTotal = hasRoadsAndSewers
    ? tenderState.calculationResults?.projectTotals?.grandTotal || 0
    : 0;

  // Calculate combined tender total
  const tenderTotal = calculateTenderTotal(
    groundworksTotal,
    roadsAndSewersTotal,
    tenderState.jobTypes
  );


  return {
    groundworksTotal,
    roadsAndSewersTotal,
    tenderTotal,
  };
};
